import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { SendingMediaSchema } from 'types/sending-media';
import { z } from 'zod/v4';

const URL = '/invoice_sending_medium';

// Tightened v2 shape: the legacy `SendingMediumSchema` carries a `z.any()`
// `force_sending` and an optional `order`. The v2 endpoint always renders real
// booleans and integer orders, so both are required here.
export const SendingMediumV2Schema = z.object({
  name: SendingMediaSchema,
  order: z.number().int(),
  forceSending: z.boolean(),
});

const InvoiceSendingMediumSchema = z.object({
  sendingMedium: z.array(SendingMediumV2Schema),
});

export type InvoiceSendingMedium = z.infer<typeof InvoiceSendingMediumSchema>;

export async function loadInvoiceSendingMediumQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = InvoiceSendingMediumSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'invoiceSendingMedium');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadInvoiceSendingMedium(options: QueryOptions<InvoiceSendingMedium> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<InvoiceSendingMedium, ApiError>({
    queryKey: ['invoiceSendingMedium'],
    queryFn: () => loadInvoiceSendingMediumQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<InvoiceSendingMedium, ApiError, 'invoiceSendingMedium'>(
    'invoiceSendingMedium',
    queryResult,
  );
}
